import { useEffect } from "react";
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from "react-router-dom";
import * as Sentry from "@sentry/react";

const hasMetrics = typeof Sentry?.metrics?.count === "function";

function hasSentryDsn() {
  return Boolean(import.meta.env.VITE_SENTRY_DSN);
}

/**
 * API origins to attach sentry-trace / baggage (distributed tracing to backend).
 */
function getTracePropagationTargets() {
  const defaults = ["localhost", /^https?:\/\/127\.0\.0\.1(:\d+)?$/i, /^https?:\/\/localhost(:\d+)?$/i];
  const base = import.meta.env.VITE_API_BASE_URL || "http://localhost:8787";
  try {
    const origin = new URL(base).origin;
    return [origin, ...defaults];
  } catch {
    return defaults;
  }
}

/**
 * Normalize path for metric attributes (avoid unbounded cardinality from raw IDs).
 */
export function coarseRoutePath(pathname) {
  if (!pathname || typeof pathname !== "string") return "/";
  return (
    pathname
      .split("/")
      .map((seg) => {
        if (!seg) return seg;
        if (/^[0-9a-f-]{36}$/i.test(seg)) return ":id";
        if (/^\d+$/.test(seg)) return ":id";
        return seg;
      })
      .join("/") || "/"
  );
}

/**
 * Initialize Sentry for error tracking, tracing, replay, and logs.
 * Call this early in the app lifecycle (before rendering).
 */
export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const environment =
    import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE || "development";
  const release = import.meta.env.VITE_SENTRY_RELEASE;

  if (!dsn) {
    console.warn("Sentry DSN not configured. Error tracking disabled.");
    return;
  }

  Sentry.init({
    dsn,
    environment,
    // Set via VITE_SENTRY_RELEASE and/or vite.config define (CF_PAGES_COMMIT_SHA on Cloudflare builds).
    release: release || undefined,
    sendDefaultPii: true,
    enableLogs: true,
    // Custom metrics (Sentry.metrics.*); defaults on in SDK but explicit for clarity.
    enableMetrics: true,

    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
    profilesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,

    // Session Replay (not "snapshots" in Sentry UI — look under Replay).
    replaysSessionSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
    replaysOnErrorSampleRate: 1.0,

    tracePropagationTargets: getTracePropagationTargets(),

    integrations: [
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    ignoreErrors: [
      "top.GLOBALS",
      "originalCreateNotification",
      "canvas.contentDocument",
      "MyApp_RemoveAllHighlights",
      "atomicFindClose",
      "fb_xd_fragment",
      "bmi_SafeAddOnload",
      "EBCallBackMessageReceived",
      "NetworkError",
      "Network request failed",
      "login_required",
      "consent_required",
    ],

    denyUrls: [/extensions\//i, /^chrome:\/\//i, /^chrome-extension:\/\//i],
  });
}

/**
 * Increment a UI-prefixed counter metric (Sentry Metrics).
 */
export function incrementUiMetric(name, value = 1, attributes = {}) {
  if (!hasSentryDsn() || !hasMetrics) return;
  try {
    const opts = Object.keys(attributes).length > 0 ? { attributes } : undefined;
    Sentry.metrics.count(name, value, opts);
  } catch {
    // best-effort
  }
}

/**
 * Record a distribution metric (e.g. durations).
 */
export function distributionUiMetric(name, value, attributes = {}) {
  if (!hasSentryDsn() || typeof Sentry.metrics?.distribution !== "function") return;
  try {
    const opts = Object.keys(attributes).length > 0 ? { attributes } : undefined;
    Sentry.metrics.distribution(name, value, opts);
  } catch {
    // best-effort
  }
}

/**
 * Attach backend Sentry event id from API error JSON for support correlation.
 */
export function attachApiErrorContext(errData, path, status) {
  if (!hasSentryDsn() || !errData?.sentryEventId) return;

  Sentry.setContext("api_error", {
    sentryEventId: errData.sentryEventId,
    path,
    status,
  });
  Sentry.addBreadcrumb({
    category: "api",
    type: "http",
    level: "error",
    message: `API error ${status}`,
    data: { sentryEventId: errData.sentryEventId, path },
  });
}

/**
 * Send a one-shot bundle of test telemetry (Sentry Logs, messages, metrics, Session Replay flush)
 * so you can verify the project in the Sentry UI. Safe to call from a dev-only button.
 *
 * @returns {{ ok: true } | { ok: false, reason: string }}
 */
export function runSentrySelfTest() {
  if (!hasSentryDsn()) {
    return { ok: false, reason: "no_dsn" };
  }

  Sentry.withScope((scope) => {
    scope.setTag("sentry_self_test", "true");
    scope.setContext("sentry_self_test", {
      page: "admin_env",
      ts: new Date().toISOString(),
    });

    const logInfo = Sentry.logger?.info;
    if (typeof logInfo === "function") {
      logInfo.call(Sentry.logger, "Admin Sentry self-test: structured log (info)", {
        kind: "structured_log",
      });
    }

    Sentry.captureMessage("Admin Sentry self-test: info-level message", {
      level: "info",
    });

    Sentry.captureMessage(
      "Admin Sentry self-test: error-level message (intentional, not a thrown Error)",
      {
        level: "error",
        fingerprint: ["admin-sentry-self-test", "error-message"],
      }
    );

    incrementUiMetric("ui.sentry_self_test", 1, { page: "admin_env" });

    Sentry.addBreadcrumb({
      category: "sentry.self_test",
      message: "Self-test events queued",
      level: "info",
    });
  });

  try {
    const replay =
      typeof Sentry.getReplay === "function" ? Sentry.getReplay() : null;
    if (replay && typeof replay.flush === "function") {
      replay.flush();
    }
  } catch {
    // best-effort
  }

  void Sentry.flush(2000);
  return { ok: true };
}

/**
 * Set user context for Sentry
 */
export function setSentryUser(user) {
  if (!hasSentryDsn()) return;

  Sentry.setUser({
    id: user?.sub || user?.id,
    email: user?.email,
    username: user?.nickname || user?.name,
  });
}

/**
 * Clear user context for Sentry
 */
export function clearSentryUser() {
  if (!hasSentryDsn()) return;
  Sentry.setUser(null);
}

/**
 * Report an auth failure to the console and Sentry (when configured).
 */
export function reportAuthFailure(step, message, extra = {}) {
  const text = message || "Authentication failed";
  console.error(`[auth:${step}]`, text, extra);

  if (!hasSentryDsn()) return;

  Sentry.withScope((scope) => {
    scope.setTag("auth_step", step);
    scope.setContext("auth_failure", { message: text, ...extra });
    Sentry.captureMessage(`Auth failed: ${step} — ${text}`, { level: "warning" });
  });
}
